'use client';

import { Children, useMemo, useRef, type ReactNode } from 'react';
import { ScrollFadeSlot } from '@/components/common/ScrollFadeSlot';
import { useWheelToHorizontalScroll } from '@/hooks/useWheelToHorizontalScroll';
import { cn } from '@/lib/utils';

type Props = {
  children: ReactNode;
  /** Extra classes for the scrolling container itself. */
  className?: string;
  /** Extra classes for each card slot. */
  cardClassName?: string;
};

/**
 * Native horizontal scroll rail with the scrollbar hidden. Each child sits in
 * a fixed-width slot that stretches to the rail's height and delegates the
 * scroll-driven fade to `ScrollFadeSlot` — the rail owns layout (sizing,
 * scroll snap, height stretch), the fade wrapper owns the animation.
 */
export function ScrollRail({ children, className, cardClassName }: Props) {
  const railRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => Children.toArray(children), [children]);

  // Desktop mouse-wheel users have no visible scrollbar to grab; the hook
  // translates a vertical wheel tick into a smooth horizontal scroll while
  // leaving trackpad swipes (which produce `deltaX`) alone.
  useWheelToHorizontalScroll(railRef);

  return (
    <div
      ref={railRef}
      className={cn(
        'flex w-full snap-x snap-mandatory gap-6 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {items.map((child, index) => (
        <ScrollFadeSlot
          key={index}
          root={railRef}
          // Sizing:
          //   • Mobile keeps a fixed `w-[300px]` so the next card always peeks
          //     from the right edge of the viewport regardless of content
          //     length. In this mode the shell also lets the CTA label wrap
          //     onto two lines when it doesn't fit (see the CTA className in
          //     `src/slices/SideHeaderCardsRow/index.tsx`).
          //   • From `sm` up, `w-fit min-w-* max-w-[440px]` lets each card
          //     sit at the floor for short content and grow up to the cap
          //     when a `whitespace-nowrap` CTA needs more room. The rail is
          //     `overflow-x-auto`, so the extra width just extends the
          //     scrollable track without leaking to the page.
          // Height:
          //   • `flex flex-col` + `[&>*]:flex-1` makes the slot's single
          //     child card stretch to fill the slot's height. The rail is a
          //     flex row with the default `align-items: stretch`, so every
          //     slot already grows to the tallest card; without this the
          //     card would keep its content height and float at the top of
          //     a taller slot. Cards used elsewhere in a static grid
          //     (`HeadlineDescriptionCtaSteps redesignCardsHorizontal`)
          //     ignore these classes since they don't sit in a flex column.
          className={cn(
            'flex w-[300px] shrink-0 snap-start flex-col sm:w-fit sm:min-w-[320px] sm:max-w-[440px] md:min-w-[360px] [&>*]:flex-1',
            cardClassName,
          )}
        >
          {child}
        </ScrollFadeSlot>
      ))}
    </div>
  );
}
